"use client";

// The Spatial Command Center - Where AI Consciousness Meets Human Intent
// Adapted from Karma AC v11 - Canvas where tiles live, connect, and evolve

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SpatialTile, { SpatialTileData } from "./SpatialTile";
import MindalooChatTile from "@/components/karma-ac/MindalooChatTile";
import { eventBus } from "@/lib/utils/eventBus";
import { haptics } from "@/lib/utils/haptics";
import type { AiProfile } from "@/types/ai";

interface Props {
  accent?: string;
  theme?: "night" | "day";
  profile?: AiProfile | null;
  className?: string;
}

/**
 * Recomputes x/y/width/height (and un-maximizes) every open, non-minimized
 * tile so they're actually visible side-by-side instead of the previous
 * behavior: tiles only ever got a one-time diagonal spawn offset and were
 * never repositioned afterward — there was no way to see more than one tile
 * at once on mobile, where every tile spawns `maximized: true` (fullscreen).
 */
function arrangeTiles(tiles: SpatialTileData[], mode: "grid" | "stack" | "scatter"): SpatialTileData[] {
  if (typeof window === "undefined") return tiles;

  const openTiles = tiles.filter((t) => !t.minimized);
  if (openTiles.length === 0) return tiles;

  const isMobile = window.innerWidth <= 767;
  const canvasWidth = window.innerWidth - 40;
  const canvasHeight = window.innerHeight - 170;
  const gap = 16;

  if (mode === "grid") {
    // minTileWidth was floor-dividing a ~335-390px mobile canvas down to 1
    // column for exactly 2 tiles (260px doesn't fit twice), so "grid" quietly
    // stacked them vertically on phones instead of the half/half split it
    // gives on desktop — confirmed live, screenshots showed 4 linked tiles
    // stacked in a single column, not side by side. Exactly 2 tiles always
    // gets 2 columns, cramped or not — the point is an actual split view,
    // and two chat tiles are still usable narrow. 3+ falls back to the
    // width-constrained math since cramming everyone into one row stops
    // being readable.
    const minTileWidth = isMobile ? 260 : 320;
    const cols = openTiles.length === 2
      ? 2
      : Math.max(1, Math.min(openTiles.length, Math.floor((canvasWidth + gap) / (minTileWidth + gap))));
    const rows = Math.ceil(openTiles.length / cols);
    const tileWidth = Math.floor((canvasWidth - gap * (cols - 1)) / cols);
    const tileHeight = Math.floor((canvasHeight - gap * (rows - 1)) / rows);

    let i = 0;
    const positioned = new Map<string, Partial<SpatialTileData>>();
    for (const tile of openTiles) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      positioned.set(tile.id, {
        maximized: false,
        x: 20 + col * (tileWidth + gap),
        y: 20 + row * (tileHeight + gap),
        width: tileWidth,
        height: tileHeight,
        zIndex: 1000 + i,
      });
      i++;
    }
    return tiles.map((t) => (positioned.has(t.id) ? { ...t, ...positioned.get(t.id) } : t));
  }

  if (mode === "scatter") {
    // A real freeform layout — previously "scatter" was just an alias for
    // "stack" (see dispatchKarmaAction), so choosing it did the same thing
    // as Stack instead of actually spreading tiles out. Randomized
    // positions/sizes/rotation-free jitter within the canvas, each tile
    // fully on-screen and not stacked directly on another's exact origin.
    const scatterWidth = isMobile ? canvasWidth - 40 : Math.min(420, canvasWidth * 0.55);
    const scatterHeight = isMobile ? Math.min(420, canvasHeight * 0.55) : Math.min(440, canvasHeight * 0.6);
    const maxX = Math.max(20, canvasWidth - scatterWidth - 20);
    const maxY = Math.max(20, canvasHeight - scatterHeight - 20);

    let i = 0;
    const positioned = new Map<string, Partial<SpatialTileData>>();
    for (const tile of openTiles) {
      positioned.set(tile.id, {
        maximized: false,
        x: 20 + Math.round(Math.random() * maxX),
        y: 20 + Math.round(Math.random() * maxY),
        width: scatterWidth,
        height: scatterHeight,
        zIndex: 1000 + i,
      });
      i++;
    }
    return tiles.map((t) => (positioned.has(t.id) ? { ...t, ...positioned.get(t.id) } : t));
  }

  // Stack mode: uniform size, cascading diagonal offset, most recent on top.
  const stackWidth = Math.min(canvasWidth - 40, isMobile ? canvasWidth - 40 : 460);
  const stackHeight = Math.min(canvasHeight - 40, isMobile ? canvasHeight - 60 : 560);
  const step = 30;
  const maxOffsetSteps = Math.max(1, Math.floor(Math.min(canvasWidth - stackWidth, canvasHeight - stackHeight) / step));

  let i = 0;
  const positioned = new Map<string, Partial<SpatialTileData>>();
  for (const tile of openTiles) {
    const offsetIndex = i % (maxOffsetSteps + 1);
    positioned.set(tile.id, {
      maximized: false,
      x: 20 + offsetIndex * step,
      y: 20 + offsetIndex * step,
      width: stackWidth,
      height: stackHeight,
      zIndex: 1000 + i,
    });
    i++;
  }
  return tiles.map((t) => (positioned.has(t.id) ? { ...t, ...positioned.get(t.id) } : t));
}

export default function SpatialCommandCenter({ accent = "#C1A2FF", theme = "night", profile, className }: Props) {
  const tileTheme = theme;
  const [tiles, setTiles] = useState<SpatialTileData[]>([]);
  // Carousel/swipe auto-activates at 3+ open tiles regardless of this flag
  // (grid/stack stop being readable well before then). Below that, it's
  // opt-in only — set true by the explicit "Swipe" arrange button so 2
  // tiles can use it without losing the side-by-side grid as the default,
  // cleared again by picking Grid/Stack/Freeform.
  const [swipeMode, setSwipeMode] = useState(false);
  const [connections, setConnections] = useState<any[]>([]);
  const canvasRef = useRef<HTMLDivElement>(null);
  const tileIdCounter = useRef(0);

  // Broadcast tile list to Atlas strip / command bar
  useEffect(() => {
    eventBus.emit("SPATIAL:TILES_COUNT", { count: tiles.filter((t) => !t.minimized).length });
    eventBus.emit("SPATIAL:TILES_LIST", {
      tiles: tiles.map((t) => ({
        id: t.id,
        name: t.name,
        icon: t.icon,
        type: t.type,
        minimized: t.minimized,
        profile: profile ?? null,
      })),
    });
    // Every windowed/maximized tile's own header+controls are hidden (see
    // SpatialTile.tsx) in favor of the consciousness bar absorbing them —
    // one adaptive bar instead of each tile drawing its own, and it always
    // reflects whichever tile is currently "active": the maximized tile if
    // one exists, otherwise the front-most (highest zIndex) windowed tile.
    // Clicking a different tile brings it to front (onMoveFront below),
    // which shifts this the same way tapping a different app shifts an
    // iPhone's dynamic island.
    const visible = tiles.filter((t) => !t.minimized);
    const active =
      visible.find((t) => t.maximized) ??
      visible.reduce<SpatialTileData | null>(
        (front, t) => (!front || t.zIndex > front.zIndex ? t : front),
        null
      );
    eventBus.emit("SPATIAL:ACTIVE_TILE_CHANGED", {
      tile: active
        ? {
            id: active.id,
            name: active.name,
            icon: active.icon,
            maximized: !!active.maximized,
            // Every tile on this canvas currently renders through the same
            // shared profile (see the `profile` prop below) — there's no
            // per-tile branding yet, so this just tracks that one accent.
            // Wiring it through the event now means the bar's ring will
            // already follow tiles individually the day they can carry
            // their own color (e.g. via the sticky-note color picker).
            color: profile?.brand?.primaryColor ?? null,
          }
        : null,
    });
    // A tile's urgency glow is "seen" the moment it becomes the active one —
    // clear it here rather than requiring an explicit dismiss action.
    if (active?.hasUnseen) {
      setTiles((prev) => prev.map((t) => (t.id === active.id ? { ...t, hasUnseen: false } : t)));
    }
  }, [tiles, profile]);

  // Handle restore / remove requests from Atlas strip chips
  useEffect(() => {
    const unsubRestore = eventBus.on("SPATIAL:RESTORE_TILE", ({ id }: { id: string }) => {
      // Un-minimizing alone left "which tile is active" (see the zIndex
      // tracking above, driven by onMoveFront) completely unaware this
      // happened — picking a tile from the tile-stack list looked like it
      // switched to it, but the composer kept targeting whatever was
      // frontmost before, since that's the only thing this used to touch.
      // Bringing it to front here is what actually makes the switch real.
      setTiles((prev) => {
        const maxZ = prev.reduce((max, t) => Math.max(max, t.zIndex), 0);
        return prev.map((t) => (t.id === id ? { ...t, minimized: false, zIndex: maxZ + 1 } : t));
      });
    });
    const unsubRemove = eventBus.on("SPATIAL:REMOVE_TILE", ({ id }: { id: string }) => {
      setTiles((prev) => prev.filter((t) => t.id !== id));
    });
    const unsubArrange = eventBus.on("SPATIAL:ARRANGE_TILES", ({ mode }: { mode: "grid" | "stack" | "scatter" | "carousel" }) => {
      if (mode === "carousel") {
        setSwipeMode(true);
      } else {
        setSwipeMode(false);
        setTiles((prev) => arrangeTiles(prev, mode));
      }
      haptics.click();
    });
    // A tile's own response finished while it wasn't the active one (see
    // MindalooChatTile's onMessageComplete) — drives the urgency glow pulse
    // in SpatialTile.tsx until the tile becomes active again (cleared above).
    const unsubUnseen = eventBus.on("SPATIAL:TILE_HAS_UNSEEN", ({ id }: { id: string }) => {
      setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, hasUnseen: true } : t)));
    });
    // Every tile used to be permanently named after its profile with no way
    // to tell two open instances of the same AI apart beyond the auto-
    // numbering in openProfileOnCanvas — this makes that actually editable.
    const unsubRename = eventBus.on("SPATIAL:RENAME_TILE", ({ id, name }: { id: string; name: string }) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, name: trimmed } : t)));
    });
    // Controls absorbed into the consciousness bar for the active tile (see
    // KarmaDynamicIsland.tsx) dispatch back through this single event rather
    // than needing their own direct references to the tile's handlers.
    // toggleSticky isn't handled here — MindalooChatTile listens for it
    // directly, since sticky-note mode is that content's own local state,
    // not something the tile-position/size model here knows about.
    const unsubActiveAction = eventBus.on(
      "SPATIAL:ACTIVE_TILE_ACTION",
      ({ id, action }: { id: string; action: "toggleMaximize" | "minimize" | "close" | "toggleSticky" }) => {
        if (action === "close") {
          setTiles((prev) => prev.filter((t) => t.id !== id));
          haptics.success();
        } else if (action === "minimize") {
          setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, minimized: true, maximized: false } : t)));
        } else if (action === "toggleMaximize") {
          setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, maximized: !t.maximized } : t)));
        }
      }
    );
    return () => {
      unsubRestore();
      unsubRemove();
      unsubArrange();
      unsubActiveAction();
      unsubUnseen();
      unsubRename();
    };
  }, []);

  // Listen for tiles being dropped into the canvas
  useEffect(() => {
    const unsubDrop = eventBus.on("SPATIAL:DROP_TILE", ({ tile, position }: { tile: any; position?: { x: number; y: number } }) => {
      console.log("[Spatial] Tile dropped:", tile);

      const isMobile = window.innerWidth <= 767;
      const isTablet = window.innerWidth > 767 && window.innerWidth <= 1024;

      const defaultWidth = isMobile
        ? Math.min(window.innerWidth - 24, 380)
        : isTablet
          ? Math.min(window.innerWidth * 0.42, 420)
          : tile.type === "chat" ? 360 : 400;

      const defaultHeight = isMobile
        ? Math.min(window.innerHeight * 0.65, 520)
        : isTablet
          ? Math.min(window.innerHeight * 0.6, 560)
          : tile.type === "chat" ? 480 : 300;

      const canvasWidth = window.innerWidth - 40;
      const canvasHeight = window.innerHeight - 170;

      const centerX = (canvasWidth / 2) - (defaultWidth / 2);
      const centerY = (canvasHeight / 2) - (defaultHeight / 2);

      const tileIndex = tiles.length;
      const offsetX = tileIndex * 30;
      const offsetY = tileIndex * 30;

      const smartX = position?.x || Math.max(20, Math.min(centerX + offsetX, canvasWidth - defaultWidth - 20));
      const smartY = position?.y || Math.max(20, Math.min(centerY + offsetY, canvasHeight - defaultHeight - 20));

      const newTile: SpatialTileData = {
        ...tile,
        id: tile.id || `tile-${Date.now()}-${tileIdCounter.current++}`,
        x: smartX,
        y: smartY,
        width: tile.width || defaultWidth,
        height: tile.height || defaultHeight,
        maximized: isMobile || tile.maximized,
        zIndex: 1000 + tiles.length,
      };

      setTiles((prev) => {
        // Numbering ("Mindaloo AC 2", "Mindaloo AC 3", ...) has to be
        // computed from prev, not from whatever count the caller saw
        // before this update ran — two tile-open triggers firing close
        // together (e.g. a manual click and a voice-pilot action) both
        // read the same stale count otherwise, producing two tiles with
        // the identical name. This is the only place guaranteed to see
        // the true latest list no matter how many creations are in flight.
        //
        // Building a fresh object here (never mutating the newTile that
        // was captured from the outer closure) matters more than it looks:
        // React 18 Strict Mode double-invokes updater functions in dev to
        // catch exactly this kind of impurity, and mutating a shared
        // outer-scope object across those invocations was actually
        // corrupting which tile a given name/conversation ended up
        // attached to — confirmed live by reading the two tiles' saved
        // messages straight out of IndexedDB and finding them swapped.
        //
        // Matching by base display name (not profile.id) is deliberate:
        // not every chat-tile creation path attaches a profile object (the
        // command-bar "quick command" path below builds a chat tile with
        // just a name, no profile) — matching on id alone made those tiles
        // invisible to this count, so a tile opened afterward via the shelf
        // landed on the same unnumbered name instead of "Mindaloo AC 2".
        const numberedName = (() => {
          if (newTile.type !== "chat") return newTile.name;
          const baseName = newTile.profile?.name || newTile.name;
          if (!baseName) return newTile.name;
          const stripTrailingNumber = (n: string) => n.replace(/\s+\d+$/, "");
          const matching = prev.filter(
            (t) => t.type === "chat" && stripTrailingNumber(t.name || "") === baseName
          );
          if (matching.length === 0) return baseName;
          // The highest existing suffix, not the match count, decides the
          // next number — counting alone breaks the moment a tile closes
          // out of order: with "Mindaloo AC", "Mindaloo AC 2", and
          // "Mindaloo AC 3" open, closing "Mindaloo AC 2" drops the count
          // to 2, so the next new tile is named "Mindaloo AC 3" again —
          // colliding with the one still on screen. Confirmed live via the
          // tile-link panel showing "Mindaloo AC 3" listed twice.
          let maxSuffix = 1; // the unnumbered base name itself counts as "1"
          for (const t of matching) {
            const m = (t.name || "").match(/\s+(\d+)$/);
            const n = m ? parseInt(m[1], 10) : 1;
            if (n > maxSuffix) maxSuffix = n;
          }
          return `${baseName} ${maxSuffix + 1}`;
        })();
        const next = [...prev, { ...newTile, name: numberedName }];
        const visibleCount = next.filter((t) => !t.minimized).length;
        // 3+ open switches to the horizontal carousel (see the render
        // section below) regardless of platform. On mobile this now also
        // applies at exactly 2: every tile spawns maximized there (right
        // for exactly one), and mobile has no grid layout to fall back to
        // (see the !isMobile check below) — so a 2nd tile used to just
        // maximize on top of the 1st with zero visible indication either
        // still existed, reachable only via a hidden "swipe the header"
        // gesture. That read as data loss, not a feature. Un-maximizing
        // into the same visible, swipeable carousel 3+ tiles already use
        // fixes it — the carousel doesn't use x/y/width/height at all, so
        // arrangeTiles's math is skipped either way.
        if (visibleCount >= 3 || (isMobile && visibleCount >= 2)) {
          return next.map((t) => (t.minimized ? t : { ...t, maximized: false }));
        }
        // A 30px diagonal cascade was never enough separation for
        // full-size (360-420px) windowed tiles — two of them just overlap.
        // Auto-grid as soon as there's a second tile to actually arrange
        // instead of leaving it stacked (desktop/tablet only — mobile is
        // handled above, straight into the carousel instead of a grid
        // that doesn't fit a narrow screen).
        if (!isMobile && visibleCount >= 2) {
          return arrangeTiles(next, "grid");
        }
        return next;
      });
      // The tile itself appearing on screen (plus the haptic) is already the
      // confirmation — a full-width toast banner on top of it for something
      // this visible was pure noise.
      haptics.success();
    });

    // Quick command from command bar / orb talks to all open chat tiles (group chat / command deck)
    // If no chat tiles exist, create one so the user still gets a response.
    const unsubQuick = eventBus.on("SPATIAL:QUICK_COMMAND", ({ text }: { text: string }) => {
      const chatTiles = tiles.filter((t) => t.type === "chat");
      if (chatTiles.length > 0) {
        eventBus.emit("SPATIAL:TILE_BROADCAST", {
          message: text,
          tileIds: chatTiles.map((t) => t.id),
        });
      } else if (profile) {
        const tileId = `quick-${Date.now()}-${tileIdCounter.current++}`;
        eventBus.emit("SPATIAL:DROP_TILE", {
          tile: {
            id: tileId,
            type: "chat",
            name: profile.name || "Mindaloo",
            icon: "💬",
            metadata: { prompt: text },
            chromeless: true,
            content: (
              <MindalooChatTile
                profile={profile}
                initialPrompt={text}
                tileId={tileId}
                theme={tileTheme}
              />
            ),
          },
        });
      }
    });

    // Swipe left/right on a maximized tile header cycles between maximized tiles.
    const unsubSwipe = eventBus.on(
      "SPATIAL:TILE_SWIPE",
      ({ direction, tileId }: { direction: "left" | "right"; tileId: string }) => {
        cycleMaximizedTile(direction, tileId);
      }
    );

    return () => {
      unsubDrop();
      unsubQuick();
      unsubSwipe();
    };
  }, [tiles.length, profile]);

  // Mobile edge swipes on the canvas open the Neural Shelf (left) or Settings (right).
  useEffect(() => {
    if (typeof window === "undefined" || "__mindaloo_edge_swipe__" in window) return;
    (window as any).__mindaloo_edge_swipe__ = true;

    let startX = 0;
    let startY = 0;
    const EDGE_THRESHOLD = 24;
    const SWIPE_MIN = 60;

    const onStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onEnd = (e: TouchEvent) => {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - startX;
      const dy = endY - startY;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dy) > Math.abs(dx)) return;
      const width = window.innerWidth;
      if (startX < EDGE_THRESHOLD && dx > 0) {
        haptics.click();
        eventBus.emit("SHELF:OPEN");
      } else if (startX > width - EDGE_THRESHOLD && dx < 0) {
        haptics.click();
        eventBus.emit("SETTINGS:OPEN");
      }
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend", onEnd);
      delete (window as any).__mindaloo_edge_swipe__;
    };
  }, []);

  // Handle drag and drop from marketplace
  useEffect(() => {
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      const data = e.dataTransfer?.getData("application/json");
      if (data) {
        try {
          const tile = JSON.parse(data);
          const position = { x: e.clientX - 200, y: e.clientY - 100 };
          eventBus.emit("SPATIAL:DROP_TILE", { tile, position });
        } catch (err) {
          console.error("Failed to parse dropped tile:", err);
        }
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener("drop", handleDrop);
      canvas.addEventListener("dragover", handleDragOver);
    }

    return () => {
      if (canvas) {
        canvas.removeEventListener("drop", handleDrop);
        canvas.removeEventListener("dragover", handleDragOver);
      }
    };
  }, []);


  const handleClose = (id: string) => {
    setTiles((prev) => prev.filter((t) => t.id !== id));
    haptics.success();
  };

  const handleResize = (id: string, width: number, height: number) => {
    setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, width, height } : t)));
  };

  const handleMoveFront = (id: string) => {
    setTiles((prev) => {
      const maxZ = Math.max(...prev.map((t) => t.zIndex));
      return prev.map((t) => (t.id === id ? { ...t, zIndex: maxZ + 1 } : t));
    });
  };

  const handleDragPosition = (id: string, x: number, y: number) => {
    setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, x, y } : t)));
  };

  const cycleMaximizedTile = (direction: "left" | "right", currentId: string) => {
    const maximized = tiles.filter((t) => t.maximized && !t.minimized);
    if (maximized.length <= 1) return;
    const ids = maximized.map((t) => t.id);
    const currentIndex = ids.indexOf(currentId);
    if (currentIndex === -1) return;
    const nextIndex =
      direction === "left"
        ? (currentIndex + 1) % ids.length
        : (currentIndex - 1 + ids.length) % ids.length;
    const nextId = ids[nextIndex];
    setTiles((prev) =>
      prev.map((t) => ({
        ...t,
        maximized: t.id === nextId,
      }))
    );
    haptics.click();
  };

  const handleSnap = (id: string, x: number, y: number, threshold: number) => {
    const otherTiles = tiles.filter((t) => t.id !== id && !t.minimized);
    for (const other of otherTiles) {
      const dx = Math.abs(x - other.x);
      const dy = Math.abs(y - other.y);
      if (dx < threshold && dy < threshold) {
        return { x: other.x, y: other.y };
      }
    }
    return null;
  };

  return (
    <div
      ref={canvasRef}
      className={`spatial-command-center relative w-full h-full overflow-hidden ${className || ""}`}
      style={{
        background:
          theme === "night"
            ? "transparent"
            : "linear-gradient(135deg, #f6f7ff 0%, #ebe7ff 100%)",
      }}
    >
      {/* Subtle grid guide */}
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px",
        }}
      />

      {/* Render non-minimized tiles. 3+ open (and none of them maximized —
          a maximized tile already takes over the whole screen on its own,
          fixed-positioned, regardless of how many others exist) switches to
          a horizontally scrollable filmstrip instead of the grid/stack/
          scatter math above: cramming "heaps" of tiles into that layout
          stops being readable well before 3, and the ask was literally
          "scroll them right on the main feed" — each tile close to full-
          width/height and swipeable, not shrunk to fit a grid cell. */}
      {(() => {
        const visible = tiles.filter((t) => !t.minimized);
        const anyMaximized = visible.some((t) => t.maximized);
        // Mobile at exactly 2 tiles takes the same un-maximize path as 3+
        // (see the DROP_TILE handler above) since it has no grid layout to
        // fall back to — this has to recognize that case too, or those
        // freshly-un-maximized tiles fall through to the plain
        // absolute-position branch below with no arrangement ever applied.
        const isMobileNow = typeof window !== "undefined" && window.innerWidth <= 767;
        // A single open tile used to fall through to the plain branch below
        // at its small floating-card default (360×480-ish) — a tiny panel
        // adrift in an otherwise-empty canvas, nothing like how a tile
        // looks once a second or third one arrives and this same carousel
        // kicks in. The ask was for one tile to look exactly like N tiles
        // in this carousel, just with nothing to swipe to yet — so it gets
        // the identical near-fullscreen, still-a-tile-card treatment
        // (rounded corners, 20px inset — see .spatial-tile-carousel-item)
        // instead of a different, smaller, special-cased layout of its own.
        const useCarousel = !anyMaximized && (
          visible.length === 1 ||
          (visible.length >= 2 && (visible.length >= 3 || swipeMode || isMobileNow))
        );
        if (!useCarousel) {
          return (
            <AnimatePresence>
              {visible.map((tile) => (
                <SpatialTile
                  key={tile.id}
                  tile={tile}
                  onClose={handleClose}
                  onResize={handleResize}
                  onMoveFront={handleMoveFront}
                  onDragPosition={handleDragPosition}
                  onSnap={handleSnap}
                  accent={accent}
                />
              ))}
            </AnimatePresence>
          );
        }
        return (
          <div className="spatial-tile-carousel">
            <AnimatePresence>
              {visible.map((tile) => (
                <SpatialTile
                  key={tile.id}
                  tile={tile}
                  onClose={handleClose}
                  onResize={handleResize}
                  onMoveFront={handleMoveFront}
                  onDragPosition={handleDragPosition}
                  onSnap={handleSnap}
                  accent={accent}
                  carousel
                />
              ))}
            </AnimatePresence>
          </div>
        );
      })()}
    </div>
  );
}

